import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Inbox, Users, Trophy, ScrollText, Shield, LogOut, ArrowLeft, Tag, ClipboardList, Webhook, Award,
  DatabaseZap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { Loader } from "../../components/UI";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, enabled: true, exact: true },
  { to: "/admin/club-requests", label: "Club Requests", icon: Inbox, enabled: true, badge: "pending" },
  { to: "/admin/name-change-requests", label: "Name Changes", icon: Tag, enabled: true, badge: "names" },
  { to: "/admin/competitions", label: "Competitions", icon: Trophy, enabled: true },
  { to: "/admin/entry-requests", label: "Entry Requests", icon: ClipboardList, enabled: true, badge: "entries" },
  { to: "/admin/users", label: "Users", icon: Users, enabled: true },
  { to: "/admin/certificates", label: "Πιστοποιητικά", icon: Award, enabled: true },
  { to: "/admin/partners", label: "Partners", icon: Webhook, enabled: true },
  { to: "/admin/sportsbook", label: "Sportsbook", icon: Trophy, enabled: true },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText, enabled: true },
  { to: "/admin/id-migration", label: "ID Migration", icon: DatabaseZap, enabled: true },
];

export function AdminLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [pending, setPending] = useState(0);
  const [pendingNames, setPendingNames] = useState(0);
  const [pendingEntries, setPendingEntries] = useState(0);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) nav("/", { replace: true });
  }, [loading, user, nav]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/overview").then(({ data }) => {
        setPending(data.pending_club_requests || 0);
        setPendingNames(data.pending_name_changes || 0);
        setPendingEntries(data.pending_entry_requests || 0);
      }).catch(() => {});
    }
  }, [user, loc.pathname]);

  if (loading) return <Loader />;
  if (!user || user.role !== "admin") return null;

  const active = (item) => item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  return (
    <div className="min-h-screen bg-[#060606] text-slate-100 flex flex-col lg:flex-row overflow-x-hidden" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:min-h-screen border-b lg:border-b-0 lg:border-r border-[#2b2512] bg-gradient-to-b from-[#14110a] to-[#060606] shrink-0">
        <div className="p-5 flex items-center gap-2 border-b border-[#2b2512]">
          <Shield className="w-6 h-6 text-[#E5B842]" />
          <div>
            <div className="font-heading text-lg font-black uppercase tracking-tight text-slate-50 leading-none">WSTF Admin</div>
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#E5B842] mt-1">Control Center</div>
          </div>
        </div>
        <nav className="p-3 flex lg:flex-col flex-wrap gap-1" data-testid="admin-nav">
          {NAV.map((it) => {
            const Icon = it.icon;
            const cls = active(it)
              ? "bg-[#E5B842] text-black"
              : it.enabled ? "text-slate-300 hover:bg-[#1b1b1b] hover:text-[#E5B842]" : "text-slate-600 cursor-not-allowed";
            const inner = (
              <span lang="el" className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide whitespace-nowrap ${cls}`}>
                <Icon className="w-4 h-4 shrink-0" /> {it.label}
                {it.badge === "pending" && pending > 0 && (
                  <span data-testid="nav-pending-badge" className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active(it) ? "bg-black/20 text-black" : "bg-[#E5B842] text-black"}`}>{pending}</span>
                )}
                {it.badge === "names" && pendingNames > 0 && (
                  <span data-testid="nav-names-badge" className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active(it) ? "bg-black/20 text-black" : "bg-[#E5B842] text-black"}`}>{pendingNames}</span>
                )}
                {it.badge === "entries" && pendingEntries > 0 && (
                  <span data-testid="nav-entries-badge" className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active(it) ? "bg-black/20 text-black" : "bg-[#E5B842] text-black"}`}>{pendingEntries}</span>
                )}
                {it.badge === "entries" && pendingEntries > 0 && (
                  <span data-testid="nav-entries-badge" className={`ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active(it) ? "bg-black/20 text-black" : "bg-[#E5B842] text-black"}`}>{pendingEntries}</span>
                )}
                {!it.enabled && <span className="ml-auto text-[8px] font-mono tracking-wider text-slate-600 hidden lg:inline">soon</span>}
              </span>
            );
            return it.enabled
              ? <Link key={it.to} to={it.to} data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>{inner}</Link>
              : <div key={it.to} data-testid={`admin-nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`} aria-disabled="true">{inner}</div>;
          })}
        </nav>
        <div className="p-3 lg:absolute lg:bottom-0 lg:w-64 border-t border-[#2b2512]">
          <Link to="/" className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-[#E5B842]"><ArrowLeft className="w-3.5 h-3.5" /> Back to site</Link>
          <button onClick={() => { logout(); nav("/"); }} data-testid="admin-logout" className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-rose-400 hover:text-rose-300"><LogOut className="w-3.5 h-3.5" /> Log out</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 p-5 sm:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
